import * as _ from 'lodash';
import Pusher from 'pusher-js';
import qs from 'qs';
import * as React from 'react';
import { withRouter } from 'react-router-dom';

import {
  LessonRecommendation,
  Recommendation,
  Student,
} from './interfaces';
import RecommendationsTable from './recommendationsTable';
import ReleaseMethodModal from './releaseMethodModal';
import ReportHeader from './reportHeader';
import {
  IMMEDIATE,
  TROUBLE_PROCESSING_MESSAGE,
  baseDiagnosticImageSrc,
  correctImage,
  expandIcon,
  fileDocumentIcon,
  informationIcon,
  RecommendedGlyphTooltip,
  releaseMethodToDisplayName,
} from './shared';

import { requestGet, requestPost, } from '../../../../../../modules/request/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor';
import {
  Checkbox,
  EXTRA_SMALL_ICON_BASE_SRC,
  LESSONS,
  Snackbar,
  Spinner,
  Tooltip,
  defaultSnackbarTimeout,
  helpIcon,
  pluralize,
  safeParseHtml,
} from '../../../../../Shared/index';
import ActivityDisclaimerBanner from '../../../shared/activityDisclaimerBanner';
import DemoOnboardingTour, { DEMO_ONBOARDING_DIAGNOSTIC_RECOMMENDATIONS, } from '../../../shared/demo_onboarding_tour';
import DemoGuardedButton from '../../../shared/demoGuardedButton';

const craneIllustration = <img alt="Grayscale construction crane" src={`${baseDiagnosticImageSrc}/crane-grayscale.svg`} />

const LESSONS_RECOMMENDATION_THRESHOLD = 50

const LessonRecommendation = ({ previouslyAssignedRecommendations, selections, setSelections, recommendation, }) => {
  const [isExpanded, setIsExpanded] = React.useState(false)

  const { activity_pack_id, name, students_needing_instruction, activities, percentage_needing_instruction, } = recommendation

  function toggleExpansion() {
    setIsExpanded(!isExpanded)
  }

  function toggleSelection() {
    if (selections.includes(activity_pack_id)) {
      setSelections(selections.filter(selection => selection !== activity_pack_id))
    } else {
      setSelections(selections.concat([activity_pack_id]))
    }
  }

  const isAssigned = previouslyAssignedRecommendations.includes(activity_pack_id)
  const isSelected = !!selections.find(s => s === activity_pack_id)
  const isRecommended = percentage_needing_instruction >= LESSONS_RECOMMENDATION_THRESHOLD
  const assigned = <div className="assigned">{correctImage}<span>Assigned</span></div>
  const studentNames = students_needing_instruction.join('<br/>')
  const activityRows = activities.map(activity => {
    return (
      <div className="activity-row" key={activity.name}>
        <span>{safeParseHtml(activity.name)}</span>
        <a className="quill-button outlined extra-small grey focus-on-light" href={activity.url} rel="noopener noreferrer" target="_blank">
          <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/eye.svg`} />
          <span>Preview</span>
        </a>
      </div>
    )
  })

  return (
    <section className={`lessons-recommendation ${isExpanded ? 'is-expanded' : ''} ${isRecommended && !isAssigned ? 'is-recommended-and-not-assigned' : ''}`}>
      <div className="top-row">
        <div>
          {isRecommended ? <RecommendedGlyphTooltip isSmall={true} /> : <span className="recommended-glyph-placeholder" />}
          {isAssigned ? (
            <React.Fragment>
              <span className="checkbox-placeholder" />
              <span className="lesson-name">{name}</span>
            </React.Fragment>
          ) : (
            <Checkbox label={name} onClick={toggleSelection} selected={isSelected} />
          )}
        </div>
        <div>
          {isAssigned && assigned}
          <div className="student-and-activity-count">
            <span>
              <Tooltip
                tooltipText={studentNames}
                tooltipTriggerText={informationIcon}
              />
              {students_needing_instruction.length} {pluralize(students_needing_instruction.length, 'student needs', 'students need')} instruction
            </span>
            <span aria-hidden={true}>•</span>
            <span className="activities-count">{activities.length} {pluralize(activities.length, 'lesson', 'lessons')}</span>
          </div>
          <button className="interactive-wrapper focus-on-light" onClick={toggleExpansion} type="button">{expandIcon}</button>
        </div>
      </div>
      {isExpanded && activityRows}
    </section>
  )
}

const LessonsRecommendations = ({ assigningLessonsBanner, previouslyAssignedRecommendations, recommendations, selections, setSelections, }) => {
  return (
    <div className="lessons-recommendations">
      {recommendations.map(recommendation => <LessonRecommendation key={recommendation.activity_pack_id} previouslyAssignedRecommendations={previouslyAssignedRecommendations} recommendation={recommendation} selections={selections} setSelections={setSelections} />)}
      {assigningLessonsBanner}
    </div>
  )
}

const RecommendationsButtons = ({ className, parentClassName, numberSelected, assigning, assigned, handleClickAssignActivityPacks, deselectAll, selectAll, selectAllRecommended, releaseMethod, handleClickEditReleaseMethod }) => {
  let assignButton = <button className="quill-button contained green small disabled focus-on-light" type="button">Assign activity packs</button>

  if (assigning) {
    assignButton = <button className="quill-button contained green small disabled focus-on-light" type="button">Assigning...</button>
  } else if (assigned) {
    assignButton = <button className="quill-button contained green small disabled focus-on-light" type="button">Assigned</button>
  } else if (numberSelected) {
    assignButton = <DemoGuardedButton className="quill-button contained green small focus-on-light" onClick={handleClickAssignActivityPacks} type="button">Assign activity packs</DemoGuardedButton>
  }

  let releaseMethodText
  let showReleaseMethodModalButton

  if (releaseMethod) {
    releaseMethodText = <span>Release Method: <b>{releaseMethod}</b></span>
  }

  if (releaseMethod && handleClickEditReleaseMethod) {
    showReleaseMethodModalButton = <button className="quill-button outlined small green focus-on-light" onClick={handleClickEditReleaseMethod} type="button">Edit</button>
  }

  return (
    <div className={`recommendations-buttons-container ${parentClassName}`}>
      <div className={`recommendations-buttons ${className}`}>
        <div className="selection-buttons">
          <button className="quill-button small outlined grey focus-on-light" onClick={selectAll} type="button">Select all</button>
          <button className="quill-button small outlined grey focus-on-light" onClick={selectAllRecommended} type="button">Select all recommended</button>
          <button className="quill-button small outlined grey focus-on-light" onClick={deselectAll} type="button">Deselect all</button>
        </div>
        <div className="release-method-and-assign-buttons">
          {releaseMethodText}
          {showReleaseMethodModalButton}
          {assignButton}
        </div>
      </div>
    </div>
  )
}

const IndependentRecommendationsButtons = ({ handleClickAssignActivityPacks, independentSelections, setIndependentSelections, recommendations, students, assigned, assigning, previouslyAssignedRecommendations, releaseMethod, setShowReleaseMethodModal, }) => {
  function handleClickEditReleaseMethod() { setShowReleaseMethodModal(true) }

  function handleSelectAllClick() {
    const newSelections = independentSelections.map((selection, index) => {
      selection.students = students.filter(s => s.completed).map(s => s.id)
      return selection
    })
    setIndependentSelections(newSelections)
  }

  function handleSelectAllRecommendedClick() {
    const newSelections = independentSelections.map((selection, index) => {
      selection.students = recommendations[index].students
      return selection
    })
    setIndependentSelections(newSelections)
  }

  function handleDeselectAllClick() {
    const newSelections = independentSelections.map(selection => {
      selection.students = []
      return selection
    })
    setIndependentSelections(newSelections)
  }

  const numberSelected = independentSelections.reduce((previousValue, selection) => {
    const previouslyAssignedActivity = previouslyAssignedRecommendations.find(r => r.activity_pack_id === selection.activity_pack_id)
    const selectedStudents = selection.students.filter(id => !previouslyAssignedActivity.students.includes(id))
    return previousValue += selectedStudents.length
  }, 0)

  return (
    <RecommendationsButtons
      assigned={assigned}
      assigning={assigning}
      className="independent-practice-recommendations-buttons"
      deselectAll={handleDeselectAllClick}
      handleClickAssignActivityPacks={handleClickAssignActivityPacks}
      handleClickEditReleaseMethod={releaseMethod && handleClickEditReleaseMethod}
      numberSelected={numberSelected}
      parentClassName="independent-practice-recommendations-buttons-container"
      releaseMethod={releaseMethodToDisplayName[releaseMethod]}
      selectAll={handleSelectAllClick}
      selectAllRecommended={handleSelectAllRecommendedClick}
    />
  )
}

const LessonsRecommendationsButtons = ({ lessonsSelections, assignLessonsActivityPacks, setLessonsSelections, lessonsRecommendations, assigned, assigning, }) => {
  function handleSelectAllClick() {
    const newSelections = lessonsRecommendations.map(r => r.activity_pack_id)
    setLessonsSelections(newSelections)
  }

  function handleSelectAllRecommendedClick() {
    const newSelections = lessonsRecommendations.filter(r => r.percentage_needing_instruction >= 50).map(r => r.activity_pack_id)
    setLessonsSelections(newSelections)
  }

  function handleDeselectAllClick() {
    setLessonsSelections([])
  }

  return (
    <RecommendationsButtons
      assigned={assigned}
      assigning={assigning}
      deselectAll={handleDeselectAllClick}
      handleClickAssignActivityPacks={assignLessonsActivityPacks}
      numberSelected={lessonsSelections.length}
      releaseMethod="Unlocked by Teacher"
      selectAll={handleSelectAllClick}
      selectAllRecommended={handleSelectAllRecommendedClick}
    />
  )
}

export const Recommendations = ({ passedPreviouslyAssignedRecommendations, passedPreviouslyAssignedLessonRecommendations, passedIndependentRecommendations, passedLessonRecommendations, match, mobileNavigation, activityName, location, lessonsBannerIsShowable, }) => {
  const [loading, setLoading] = React.useState<boolean>(!passedPreviouslyAssignedRecommendations && !passedIndependentRecommendations && !passedLessonRecommendations);
  const [previouslyAssignedIndependentRecommendations, setPreviouslyAssignedIndependentRecommendations] = React.useState<Recommendation[]>(passedPreviouslyAssignedRecommendations);
  const [previouslyAssignedLessonsRecommendations, setPreviouslyAssignedLessonsRecommendations] = React.useState<LessonRecommendation[]>(passedPreviouslyAssignedLessonRecommendations);
  const [independentRecommendations, setIndependentRecommendations] = React.useState<Recommendation[]>(passedIndependentRecommendations);
  const [lessonsRecommendations, setLessonsRecommendations] = React.useState<LessonRecommendation[]>(passedLessonRecommendations);
  const [independentSelections, setIndependentSelections] = React.useState<Recommendation[]>([]);
  const [lessonsSelections, setLessonsSelections] = React.useState<number[]>([]);
  const [students, setStudents] = React.useState<Student[]>([]);
  const [independentAssigning, setIndependentAssigning] = React.useState(false)
  const [independentAssigned, setIndependentAssigned] = React.useState(false)
  const [lessonsAssigning, setLessonsAssigning] = React.useState(false)
  const [lessonsAssigned, setLessonsAssigned] = React.useState(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')
  const [lessonsBannerEnabled, setLessonsBannerEnabled] = React.useState(lessonsBannerIsShowable)
  const [releaseMethod, setReleaseMethod] = React.useState(null)
  const [originalReleaseMethod, setOriginalReleaseMethod] = React.useState(null)
  const [showReleaseMethodModal, setShowReleaseMethodModal] = React.useState(false)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const { params, } = match
  const { activityId, classroomId, } = params
  const unitId = qs.parse(location.search.replace('?', '')).unit
  const unitQueryString = unitId ? `?unit_id=${unitId}` : ''

  React.useEffect(() => {
    getRecommendations()
    getPreviouslyAssignedRecommendationData()
  }, [])

  React.useEffect(() => {
    setLoading(true)
    setIndependentSelections([])
    setLessonsSelections([])
    setIndependentRecommendations(null)
    setLessonsRecommendations(null)
    setPreviouslyAssignedIndependentRecommendations(null)
    setPreviouslyAssignedLessonsRecommendations(null)
    getRecommendations()
    getPreviouslyAssignedRecommendationData()
  }, [activityId, classroomId, unitId])

  React.useEffect(() => {
    if (loading) { return }
    let newSelections = independentSelections
    if (independentSelections.length === 0) {
      newSelections = independentRecommendations.map((recommendation, i) => {
        return {
          activity_count: recommendation.activity_count,
          activity_pack_id: recommendation.activity_pack_id,
          name: recommendation.name,
          students: [...recommendation.students],
        };
      });
    }
    const newLessonsRecommendations = lessonsRecommendations.map((recommendation) => {
      if (previouslyAssignedLessonsRecommendations.includes(recommendation.activity_pack_id)) {
        return Object.assign({}, recommendation, { status: 'assigned', previously_assigned: true, });
      } else {
        return recommendation;
      }
    });
    setIndependentSelections(newSelections)
    setLessonsRecommendations(newLessonsRecommendations)
  }, [loading])

  React.useEffect(() => {
    if (independentAssigned) {
      setTimeout(() => setIndependentAssigned(false), 5000)
    }
    if (lessonsAssigned) {
      setTimeout(() => setLessonsAssigned(false), 5000)
    }
  }, [independentAssigned, lessonsAssigned])

  React.useEffect(() => {
    if (independentRecommendations && lessonsRecommendations && previouslyAssignedLessonsRecommendations && previouslyAssignedIndependentRecommendations) {
      setLoading(false)
    }
  }, [independentRecommendations, lessonsRecommendations, previouslyAssignedLessonsRecommendations, previouslyAssignedIndependentRecommendations])

  React.useEffect(() => {
    setReleaseMethod(originalReleaseMethod)
  }, [originalReleaseMethod])

  function closeLessonsBanner() {
    setLessonsBannerEnabled(false)
    requestPost('/milestones/complete_acknowledge_lessons_banner')
  }

  function getRecommendations() {
    requestGet(`/teachers/progress_reports/recommendations_for_classroom/${classroomId}/activity/${activityId}${unitQueryString}`, (data) => {
      setIndependentRecommendations(JSON.parse(JSON.stringify(data.recommendations)));
      setStudents(data.students)
    });
    requestGet(`/teachers/progress_reports/lesson_recommendations_for_classroom/${classroomId}/activity/${activityId}${unitQueryString}`, (data) => {
      setLessonsRecommendations(data.lessonsRecommendations);
    });
  }

  function getPreviouslyAssignedRecommendationData() {
    requestGet(`/teachers/progress_reports/previously_assigned_recommendations/${classroomId}/activity/${activityId}${unitQueryString}`, ((data) => {
      setPreviouslyAssignedIndependentRecommendations(data.previouslyAssignedIndependentRecommendations)
      setPreviouslyAssignedLessonsRecommendations(data.previouslyAssignedLessonsRecommendations)
      const anyIndependentRecommendationsPreviouslyAssigned = data.previouslyAssignedIndependentRecommendations.some(rec => rec.students.length)
      const releaseMethod = anyIndependentRecommendationsPreviouslyAssigned ? data.releaseMethod || IMMEDIATE : data.releaseMethod
      setOriginalReleaseMethod(releaseMethod)
    }));
  }

  function initializePusher(isLessons = false) {
    if (window.postbuildEnv.RAILS_ENV === 'development') {
      Pusher.logToConsole = true;
    }
    const pusher = new Pusher(window.postbuildEnv.PUSHER_KEY, { cluster: window.postbuildEnv.PUSHER_CLUSTER });
    const channel = pusher.subscribe(classroomId);
    if (isLessons) {
      channel.bind('lessons-recommendations-assigned', (data) => {
        triggerSnackbar('Activity packs assigned')
        setLessonsAssigned(true)
        setLessonsAssigning(false)
        getPreviouslyAssignedRecommendationData();
        pusher.unsubscribe(classroomId)
      });
    } else {
      channel.bind('personalized-recommendations-assigned', (data) => {
        triggerSnackbar('Activity packs assigned')
        setIndependentAssigned(true)
        setIndependentAssigning(false)
        getPreviouslyAssignedRecommendationData();
        pusher.unsubscribe(classroomId)
      });
    }
  }

  function triggerSnackbar(snackbarCopy) {
    setSnackbarText(snackbarCopy)
    setShowSnackbar(true)
  }

  function onClickCancelReleaseMethod() {
    setReleaseMethod(originalReleaseMethod)
    setShowReleaseMethodModal(false)
  }

  function assignLessonsActivityPacks() {
    initializePusher(true)
    requestPost('/teachers/progress_reports/assign_whole_class_instruction_packs/', { unit_template_ids: lessonsSelections, classroom_id: params.classroomId }, (data) => { }, (data) => {
      alert(TROUBLE_PROCESSING_MESSAGE);
    })
  }

  function formatSelectionsForAssignment() {
    const independentSelectionsArr = independentSelections.map((activityPack, index) => {
      const students = _.uniq(activityPack.students.concat(previouslyAssignedIndependentRecommendations[index].students))
      return {
        id: activityPack.activity_pack_id,
        classrooms: [
          {
            id: classroomId,
            student_ids: students,
            order: index
          }
        ]
      }
    });
    return { selections: independentSelectionsArr, };
  }

  function assignIndependentActivityPacks() {
    setShowReleaseMethodModal(false)
    const dataToPass = {
      classroom_id: classroomId,
      diagnostic_activity_id: activityId,
      ...formatSelectionsForAssignment(),
      assigning_all_recommended_packs: _.isEqual(independentSelections, independentRecommendations),
      release_method: releaseMethod
    }
    setIndependentAssigning(true)
    initializePusher()
    requestPost('/teachers/progress_reports/assign_independent_practice_packs/', dataToPass, (data) => { }, (data) => {
      alert(TROUBLE_PROCESSING_MESSAGE);
      setIndependentAssigning(false)
    })
  }

  function handleClickAssignIndependentActivityPacks() {
    if (releaseMethod) {
      assignIndependentActivityPacks()
    } else {
      setShowReleaseMethodModal(true)
    }
  }

  const responsesLink = (studentId: number) => unitId ? `/diagnostics/${activityId}/classroom/${classroomId}/responses/${studentId}?unit=${unitId}` : `/diagnostics/${activityId}/classroom/${classroomId}/responses/${studentId}`

  if (loading) { return <Spinner /> }

  const studentsWhoCompletedDiagnostic = students.filter(sr => sr.completed)

  let independentRecommendationsSection

  if (independentRecommendations.length) {
    independentRecommendationsSection = (<section aria-labelledby="independentPracticeHeader" className={`independent-practice ${lessonsRecommendations.length ? '' : 'no-bottom-border'}`}>
      <div className="independent-practice-header">
        <h2 id="independentPracticeHeader">Independent Practice</h2>
        <Tooltip
          tooltipText="Each cell highlighted in green is an area for growth for the student - the student did not demonstrate full proficiency in this skill on the diagnostic, and Quill recommends that the student practices this skill. You can see their exact proficiency for each skill on the student results page."
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />
      </div>
      <div className="recommendations-table-container">
        <section aria-label="Independent Practice Recommendations" className="recommendations-table-wrapper">
          <IndependentRecommendationsButtons
            assigned={independentAssigned}
            assigning={independentAssigning}
            handleClickAssignActivityPacks={handleClickAssignIndependentActivityPacks}
            independentSelections={independentSelections}
            previouslyAssignedRecommendations={previouslyAssignedIndependentRecommendations}
            recommendations={independentRecommendations}
            releaseMethod={releaseMethod}
            setIndependentSelections={setIndependentSelections}
            setShowReleaseMethodModal={setShowReleaseMethodModal}
            students={students}
          />
          <RecommendationsTable
            previouslyAssignedRecommendations={previouslyAssignedIndependentRecommendations}
            recommendations={independentRecommendations}
            responsesLink={responsesLink}
            selections={independentSelections}
            setSelections={setIndependentSelections}
            students={students}
            studentsWhoCompletedDiagnostic={studentsWhoCompletedDiagnostic}
          />
        </section>
      </div>
    </section>)
  }

  let assigningLessonsBanner

  if (lessonsSelections.length && lessonsBannerEnabled) {
    assigningLessonsBanner = <ActivityDisclaimerBanner activityType={LESSONS} closeBanner={closeLessonsBanner} />
  }

  let wholeClassInstructionSection

  if (lessonsRecommendations.length) {
    wholeClassInstructionSection = (<section aria-labelledby="wholeClassInstructionTitle" className="whole-class-instruction">
      <div className="whole-class-instruction-header">
        <h2 id="wholeClassInstructionTitle">Whole class instruction</h2>
        <Tooltip
          tooltipText="Quill recommends a Quill Lessons activity when at least 50% of the students in your class need instruction on a particular skill. Quill Lessons are teacher-led lessons that include a full lesson plan, suggested slides, real-time paired practice, and whole-class discussion time. Each lesson is focused on a specific grammar skill."
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />
      </div>
      <LessonsRecommendationsButtons assigned={lessonsAssigned} assigning={lessonsAssigning} assignLessonsActivityPacks={assignLessonsActivityPacks} lessonsRecommendations={lessonsRecommendations} lessonsSelections={lessonsSelections} setLessonsSelections={setLessonsSelections} students={students} />
      <LessonsRecommendations assigningLessonsBanner={assigningLessonsBanner} previouslyAssignedRecommendations={previouslyAssignedLessonsRecommendations} recommendations={lessonsRecommendations} selections={lessonsSelections} setSelections={setLessonsSelections} />
    </section>)
  }

  let emptyState

  if (!(independentRecommendations.length || lessonsRecommendations.length)) {
    emptyState = (<section className="recommendations-empty-state">
      {craneIllustration}
      <h2>We&#39;re working on it!</h2>
      <p>We just launched the growth diagnostics, so we don&#39;t have practice recommendations ready for the {activityName} yet. In the meantime, you can always assign the original recommendations again or explore our full library of activities to find additional practice for your students.</p>
    </section>)
  }

  return (
    <main className="diagnostic-recommendations-container">
      <ReleaseMethodModal
        handleClickAssign={assignIndependentActivityPacks}
        handleClickCancel={onClickCancelReleaseMethod}
        originalReleaseMethod={originalReleaseMethod}
        releaseMethod={releaseMethod}
        setReleaseMethod={setReleaseMethod}
        visible={showReleaseMethodModal}
      />
      <DemoOnboardingTour pageKey={DEMO_ONBOARDING_DIAGNOSTIC_RECOMMENDATIONS} />
      <Snackbar text={snackbarText} visible={showSnackbar} />
      <ReportHeader
        guideLink={!emptyState && <a className="quill-button outlined small grey focus-on-light" href="https://support.quill.org/en/articles/5698147-how-do-i-read-the-practice-recommendations-report" rel="noopener noreferrer" target="_blank">{fileDocumentIcon}<span>Guide</span></a>}
        pageName="Practice Recommendations"
        subHeader="Assign practice to students"
      />
      {mobileNavigation}
      {emptyState}
      {independentRecommendationsSection}
      {wholeClassInstructionSection}
    </main>
  )
}

export default withRouter(Recommendations)
